import { ContactItem } from "@/components/contacts/ContactItem.tsx";
import { Loader } from "@/components/loader/Loader.tsx";
import { useThreadParticipants } from "@/hooks/useContacts.tsx";
import type { Contact } from "@/types/contact.ts";
import { Link } from "wouter";

/**
 * One participant, linked to their canonical contact page. A real `<a href>`
 * rather than a click handler: the surrounding nav boundary decides whether the
 * contact drills onto the canvas, opens the sidebar, or loads the page, and
 * cmd/middle-click keeps working either way.
 */
const ParticipantRow = ({ contact }: { contact: Contact }) => (
  <Link
    href={`/contacts/${contact.id}`}
    aria-label={`Open ${contact.name || "Unnamed"}`}
    className="rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
  >
    <ContactItem contact={contact} />
  </Link>
);

/**
 * The people on a thread. The single body for that list, rendered by the canvas
 * card and the full page alike so the two can't drift — only their chrome
 * differs.
 *
 * Deliberately just the roster: it lists who is on the thread and links each
 * person to their contact. Editing contacts (merge, delete) lives on the
 * contacts screen, which a participant is one click away from.
 *
 * States render as plain inline bodies rather than full-bleed error screens, so
 * whichever surface hosts this owns the framing.
 */
export const ThreadParticipantsBody = ({ threadId }: { threadId: number }) => {
  const { participants, isLoading, error } = useThreadParticipants(threadId);

  if (isLoading) return <Loader className="py-8" />;
  if (error) {
    return <p className="p-4 text-muted-foreground text-sm">Couldn't load this thread's people.</p>;
  }

  // A contact merged into another still comes back on the thread it was on;
  // showing it would offer a row that opens the survivor under the wrong name.
  const listed = participants.filter((contact) => !contact.mergedInto);
  if (listed.length === 0) {
    return <p className="p-4 text-muted-foreground text-sm">Nobody else is on this thread.</p>;
  }

  return (
    <div className="flex flex-col gap-2">
      {listed.map((contact) => (
        <ParticipantRow key={contact.id} contact={contact} />
      ))}
    </div>
  );
};
